import { LitElement, html, css, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { getNextKeyboardFocusIndex } from './utils/keyboard-nav.js';
import { FormAssociatedMixin } from './utils/form-associated-mixin.js';
import './OlIcon.js';
import './OlTooltip.js';
import { focusRing, pressable, raised } from './control-styles.js';

/**
 * OlSegmentedControl - A single-select control styled like ol-button.
 *
 * Renders a row of mutually-exclusive options (a radio group): exactly one is
 * selected at a time. It shares ol-button's height tokens and visual language,
 * so a same-size segmented control and button line up at the same height.
 *
 * Options are declared as light-DOM <ol-segment> children carrying a `value`
 * attribute; their content is the label — plain text, or markup such as an
 * <ol-icon>. Icon-only segments must add a `label` attribute to name the radio
 * (used as the aria-label and a hover tooltip). Children are read once on connect
 * and re-rendered as accessible radios in the shadow root, so the control needs
 * no per-option wiring from the consuming page.
 *
 * Contains no application logic — the consuming page owns what a selection
 * *does*. Listen for the change event (or read `.value`).
 *
 * @element ol-segmented-control
 *
 * @prop {String}  value           - The selected option's value. Reflected; defaults to the first enabled option.
 * @prop {String}  name            - Form field name. When set, the selected `value` submits with the enclosing `<form>` (see FormAssociatedMixin).
 * @prop {"small" | "medium" | "large"} size - Default: "medium"
 * @prop {Boolean} disabled        - Disables the whole control.
 * @prop {Boolean} fullWidth       - Stretch to fill the container; options share the width equally.
 * @prop {String}  accessibleLabel - aria-label for the radio group. Default: none.
 *
 * @fires ol-segmented-control-change - Fired on user selection. detail: { value: String }
 *
 * @slot - One or more <ol-segment value="…">Label</ol-segment> option elements.
 *   A segment's content may be text or markup (e.g. an icon); add a `label`
 *   attribute on icon-only segments for the accessible name.
 *
 * @example
 *   <ol-segmented-control value="list" accessible-label="View">
 *     <ol-segment value="grid">Grid</ol-segment>
 *     <ol-segment value="list">List</ol-segment>
 *   </ol-segmented-control>
 *
 * @example
 *   <ol-segmented-control value="grid" accessible-label="View">
 *     <ol-segment value="grid" label="Grid"><ol-icon name="layout-grid"></ol-icon></ol-segment>
 *     <ol-segment value="list" label="List"><ol-icon name="list"></ol-icon></ol-segment>
 *   </ol-segmented-control>
 */
export class OlSegmentedControl extends FormAssociatedMixin(LitElement) {
    static properties = {
        value: { type: String, reflect: true },
        size: { type: String, reflect: true },
        disabled: { type: Boolean, reflect: true },
        fullWidth: { type: Boolean, reflect: true, attribute: 'full-width' },
        accessibleLabel: { type: String, attribute: 'accessible-label' },
        _options: { state: true }
    };

    static styles = [
        raised,
        pressable,
        focusRing,
        css`
            :host {
                display: inline-flex;
                vertical-align: middle;

                /* Concentric with the track: the pill sits one --spacing-3xs inside
               it, so its radius has to shed that inset or it reads rounder than
               the corner containing it. */
                --pill-radius: calc(var(--border-radius-button) - var(--spacing-3xs));

                /* A crisp ease-out (no overshoot) for the slide — the pill should
               feel like it snaps to the new segment, not bounce. */
                --slide-duration: var(--duration-base);
                --slide-ease: var(--ease-move);
            }

            :host([full-width]) {
                display: flex;
                width: 100%;
            }

            :host(:disabled) {
                opacity: 0.55;
                cursor: not-allowed;
            }

            /* A light-grey inset track. The selected segment floats inside it as a
           white pill, so the track padding sets the gap around that pill.

           Two stacked layers ride inside the track, both built from the same
           option list so they line up pixel-for-pixel:
             • .layer--base   — the real radio buttons, painted in the dimmed
               (inactive) colour. They own all interaction and a11y.
             • .layer--active — an aria-hidden duplicate painted in the active
               (dark) colour, clipped to just the selected segment.
           Sliding the clip-path of the active layer makes a segment's colour
           flip exactly where the pill's edge crosses it, so the colour change
           travels *with* the pill instead of toggling instantly. The white pill
           itself is a separate .pill element (clip-path would eat its shadow). */
            .track {
                position: relative;
                isolation: isolate;
                display: inline-flex;
                box-sizing: border-box;
                height: var(--control-height-medium);
                padding: var(--spacing-3xs);
                border-radius: var(--border-radius-button);
                background-color: var(--color-control-track);
            }

            :host([full-width]) .track {
                display: flex;
                width: 100%;
            }

            .layer {
                display: inline-flex;
            }

            /* The interactive layer sits above the pill so its text reads on top of
           the white fill; the active (clipped) layer sits above both. It's a
           flex item of the track, so align-items: stretch already sizes it to
           the track's content height — its segments fill that via height: 100%. */
            .layer--base {
                position: relative;
                z-index: var(--z-index-local-2);
                height: 100%;
            }

            :host([full-width]) .layer--base {
                flex: 1;
            }

            .layer--active {
                position: absolute;
                z-index: var(--z-index-local-3);
                inset: var(--spacing-3xs);
                display: flex;
                pointer-events: none;
                /* clip-path (revealing only the selected segment's slice of the
               dark-text layer) is set directly by _measure() so its changes
               trigger the transition — an inherited var() change would not. */
                visibility: hidden;
            }

            /* The selected segment's text. Doubled class (.segment.segment--ghost)
           so this beats the later, equal-specificity ".segment" color rule —
           otherwise the cascade resolves the ghost to --color-text-muted and the
           selected segment looks dimmed instead of full-strength #333. */
            .segment.segment--ghost {
                color: var(--color-text);
            }

            /* The sliding white pill — carries the raised look the selected segment
           used to draw itself. translateX + width walk it to the active
           segment; its shadow can't be clipped, hence its own element. */
            .pill {
                position: absolute;
                z-index: var(--z-index-local-1);
                /* border-box so _measure()'s width — a segment's border-box width —
               isn't widened by this element's own border. Without it the pill
               overhangs its segment by 2px, swallowing the track's right
               padding on the last segment. */
                box-sizing: border-box;
                top: var(--spacing-3xs);
                bottom: var(--spacing-3xs);
                left: var(--spacing-3xs);
                /* width + translateX are set directly by _measure() (see above). */
                width: 0;
                /* Border, fill and lift (the shared .raised recipe) are
               ol-button[variant="secondary"]'s, so the selected segment reads as
               the same control as the buttons beside it. The track stays a step
               lighter than this border, so the pill keeps a visible outline
               against it. */
                border: 1px solid var(--color-control-border);
                border-radius: var(--pill-radius);
                background-color: var(--color-control-bg);
                --control-surface: var(--color-control-bg);
                --control-highlight-strength: 35%;
                visibility: hidden;
            }

            /* Both are hidden until _measure() has placed them (avoids a flash of
           an unpositioned pill on first paint). */
            .track.is-ready .pill,
            .track.is-ready .layer--active {
                visibility: visible;
            }

            /* Transitions are enabled one frame after the initial placement so the
           pill appears in position instead of sliding in from the left. */
            .track.is-animated .pill {
                transition:
                    transform var(--slide-duration) var(--slide-ease),
                    width var(--slide-duration) var(--slide-ease);
            }

            .track.is-animated .layer--active {
                transition: clip-path var(--slide-duration) var(--slide-ease);
            }

            /* The tooltip wrapper around an icon segment must generate no box: the
           button has to stay a direct flex item of .layer--base so full-width
           sizing and _measure()'s rects match the ghost layer exactly. */
            ol-tooltip {
                display: contents;
            }

            .segment {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                box-sizing: border-box;
                height: 100%;
                padding: 0 var(--spacing-md);
                /* Transparent border on every segment keeps widths stable, so
               selecting one doesn't shift its neighbors when its border shows. */
                border: 1px solid transparent;
                border-radius: var(--border-radius-button);
                background-color: transparent;
                /* Non-selected segments are dimmed so the selected one reads as
               active; hover and the selected pill darken back to full strength. */
                color: var(--color-text-muted);
                font-family: var(--font-family-button);
                font-size: var(--font-size-body-medium);
                font-weight: var(--font-weight-button);
                line-height: var(--line-height-control);
                white-space: nowrap;
                cursor: pointer;
                user-select: none;
                /* The shared .focus-ring, pulled inside the segment: an outer ring
               would be clipped by the track. */
                --_focus-offset: -2px;
                /* Only the selected pill crossfades in (background/border/shadow).
               Color is intentionally NOT transitioned so hover darkening is
               instant — the hover effect must give immediate feedback. */
                transition:
                    background-color var(--duration-fast),
                    border-color var(--duration-fast),
                    box-shadow var(--duration-fast);
            }

            :host([full-width]) .segment {
                flex: 1 1 0;
            }

            /* Icon segments: size the glyph to the control and let it inherit the
           segment's color so it tracks selected/hover states like text does.
           An outer-tree rule beats <ol-icon>'s own :host size, so this wins. */
            .segment ol-icon,
            .segment svg {
                display: block;
                width: 18px;
                height: 18px;
            }

            @media (hover: hover) and (pointer: fine) {
                .segment:not([aria-checked='true']):not(:disabled):hover {
                    color: var(--color-text);
                }
            }

            /* The selected segment's raised-white-pill look now lives on the
           floating .pill, and its dark text on the clipped .layer--active — so
           the interactive base button stays dimmed and transparent even when
           selected (its text reads through from the active layer above). */

            .segment:disabled {
                opacity: 0.45;
                cursor: not-allowed;
            }

            /* Sizes — mirror ol-button so same-size controls line up. */
            :host([size='small']) .track {
                height: var(--control-height-small);
            }

            :host([size='small']) .segment {
                padding: 0 var(--spacing-sm);
                font-size: var(--font-size-label-medium);
            }

            :host([size='small']) .segment ol-icon,
            :host([size='small']) .segment svg {
                width: 16px;
                height: 16px;
            }

            :host([size='large']) .track {
                height: var(--control-height-large);
            }

            :host([size='large']) .segment {
                padding: 0 var(--spacing-lg);
                font-size: var(--font-size-body-large);
            }

            :host([size='large']) .segment ol-icon,
            :host([size='large']) .segment svg {
                width: 20px;
                height: 20px;
            }

            @media (prefers-reduced-motion: reduce) {
                .segment {
                    transition: none;
                }

                .segment:active:not(:disabled) {
                    transform: none;
                }

                /* The pill and active layer still jump straight to the selected
               segment (positioned by JS) — just without the slide. */
                .track.is-animated .pill,
                .track.is-animated .layer--active {
                    transition: none;
                }
            }
        `
    ];

    constructor() {
        super();
        this.value = null;
        this.size = 'medium';
        this.disabled = false;
        this.fullWidth = false;
        this.accessibleLabel = null;
        this._options = [];
    }

    connectedCallback() {
        super.connectedCallback();
        this._harvestOptions();
        // Capture the resolved default selection for <form>.reset() (after
        // _harvestOptions has normalized an unset/invalid value).
        if (this._defaultValue === undefined) this._defaultValue = this.value;
    }

    // ── Form participation (FormAssociatedMixin) ─────────────────────────
    // A segmented control is a radio group: it always has a selection, so it
    // always submits the selected option's value under `name`.
    /**
     * @override
     * @returns {string|null} A radio group always has a selection, so this
     *   always submits the selected option's value.
     */
    get formAssociatedValue() {
        return this.value ?? null;
    }

    /**
     * @override
     * @returns {void}
     */
    formAssociatedReset() {
        this.value = this._defaultValue;
    }

    // Read the declarative <ol-segment> children into a plain options array.
    // The light-DOM children stay in the DOM (hidden via ol-components.css) but
    // are never slotted — the shadow root renders the interactive radios.
    _harvestOptions() {
        this._options = Array.from(this.querySelectorAll('ol-segment')).map(el => {
            const text = el.textContent.trim();
            // A segment with element children carries markup (e.g. an icon);
            // render that verbatim. A text-only segment renders its plain text.
            const isMarkup = el.children.length > 0;
            return {
                value: el.getAttribute('value') ?? text,
                content: isMarkup ? el.innerHTML.trim() : text,
                isMarkup,
                // Accessible name: the `label` attribute, else the text. Icon-only
                // segments have no text, so they rely on `label`.
                accessibleLabel: el.getAttribute('label') ?? text,
                disabled: el.hasAttribute('disabled')
            };
        });

        // A segmented control always has a selection. If value is unset or
        // doesn't match an option, fall back to the first enabled option.
        const hasValid = this._options.some(o => o.value === this.value && !o.disabled);
        if (!hasValid) {
            const firstEnabled = this._options.find(o => !o.disabled);
            this.value = firstEnabled ? firstEnabled.value : null;
        }
    }

    async firstUpdated() {
        this._track = this.renderRoot.querySelector('.track');
        // Icon segments are slotted into an <ol-tooltip>, which renders its own
        // <slot> a microtask later; until it does, those buttons have no box and
        // would measure as zero-width. Wait for them before sizing the pill.
        const tooltips = Array.from(this.renderRoot.querySelectorAll('ol-tooltip'));
        await Promise.all(tooltips.map(t => t.updateComplete));
        // Place the pill/active layer, reveal them, then enable transitions one
        // frame later so the first placement doesn't animate from the origin.
        this._measure();
        this._track.classList.add('is-ready');
        requestAnimationFrame(() => this._track?.classList.add('is-animated'));
        // Re-measure on any reflow: size/full-width changes, late web-font
        // metrics, or the container resizing around a full-width control.
        this._resizeObserver = new ResizeObserver(() => this._measure());
        this._resizeObserver.observe(this._track);
        this._syncFormValue();
    }

    updated(changed) {
        // Selection moved — walk the pill and clip to the new segment.
        if (changed.has('value')) {
            this._measure();
            this._syncFormValue();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._resizeObserver?.disconnect();
    }

    // Move the pill (translateX + width) and the active layer's clip window onto
    // the selected segment. The geometry is set directly on each element (not via
    // an inherited custom property) so the changes actually trigger their CSS
    // transitions. Measured relative to the base layer, so the values are
    // independent of the track's padding and of where the control sits on the page.
    _measure() {
        const track = this._track;
        if (!track) return;
        const base = track.querySelector('.layer--base');
        const activeBtn = track.querySelector('.segment[aria-checked="true"]');
        const pill = track.querySelector('.pill');
        const activeLayer = track.querySelector('.layer--active');
        if (!base || !activeBtn || !pill || !activeLayer) return;

        const baseRect = base.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();
        const x = btnRect.left - baseRect.left;
        const w = btnRect.width;
        const right = baseRect.width - x - w;

        pill.style.transform = `translateX(${x}px)`;
        pill.style.width = `${w}px`;
        activeLayer.style.clipPath = `inset(0 ${right}px 0 ${x}px round var(--pill-radius))`;
    }

    // Index of the option that owns the roving tabindex / arrow-key focus.
    get _activeIndex() {
        const i = this._options.findIndex(o => o.value === this.value);
        if (i !== -1) return i;
        return this._options.findIndex(o => !o.disabled);
    }

    _select(value, { focus = false } = {}) {
        if (value === this.value) return;
        this.value = value;
        this.dispatchEvent(
            new CustomEvent('ol-segmented-control-change', {
                bubbles: true,
                composed: true,
                detail: { value }
            })
        );
        if (focus) {
            this.updateComplete.then(() => {
                const btn = this.renderRoot.querySelector('.segment[aria-checked="true"]');
                if (btn) btn.focus();
            });
        }
    }

    // Standard radio-group keyboard model: arrows move selection (and focus)
    // to the previous/next enabled option, wrapping; Home/End jump to the ends.
    _onKeydown(e) {
        const target = getNextKeyboardFocusIndex(e.key, {
            count: this._options.length,
            current: this._activeIndex,
            isDisabled: i => this._options[i].disabled,
            orientation: 'both',
            wrap: true
        });
        if (target === -1) return;
        e.preventDefault();
        this._select(this._options[target].value, { focus: true });
    }

    render() {
        const activeIndex = this._activeIndex;
        // The active layer mirrors the base layer one-for-one so the two stay
        // pixel-aligned — the clip can then split a label's colour mid-glyph.
        return html`
            <div class="track" role="radiogroup" aria-label=${this.accessibleLabel || nothing} @keydown=${this._onKeydown}>
                <div class="pill raised" aria-hidden="true"></div>
                <div class="layer layer--base">${this._options.map((option, i) => this._renderSegment(option, i, activeIndex))}</div>
                <div class="layer layer--active" aria-hidden="true">${this._options.map(option => this._renderGhost(option))}</div>
            </div>
        `;
    }

    // The active-layer twin of a segment: same box (so widths match the real
    // button exactly) but a plain, non-interactive span in the active colour.
    _renderGhost(option) {
        return html` <span class="segment segment--ghost" aria-hidden="true">${option.isMarkup ? unsafeHTML(option.content) : option.content}</span> `;
    }

    _renderSegment(option, i, activeIndex) {
        const checked = option.value === this.value;
        // Icon-only segments have no visible text, so name them with aria-label.
        const labelAttr = option.isMarkup ? option.accessibleLabel : nothing;
        const button = html`
            <button
                class="segment pressable focus-ring"
                type="button"
                role="radio"
                aria-checked=${checked ? 'true' : 'false'}
                aria-label=${labelAttr}
                tabindex=${i === activeIndex ? '0' : '-1'}
                ?disabled=${this.isDisabled || option.disabled}
                @click=${() => this._select(option.value)}
            >
                ${option.isMarkup ? unsafeHTML(option.content) : option.content}
            </button>
        `;
        // An icon alone doesn't say what it does, so repeat its name in a
        // tooltip. Below the control: these sit in toolbars where the space
        // above usually belongs to another row.
        if (!option.isMarkup || !option.accessibleLabel) return button;
        return html` <ol-tooltip content=${option.accessibleLabel} placement="bottom">${button}</ol-tooltip> `;
    }
}

customElements.define('ol-segmented-control', OlSegmentedControl);
